import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./context/AuthContext";
import { AudioPlayerProvider } from "./context/AudioPlayerContext";
import { ThemeProvider } from "./context/ThemeContext";
import { DateFormatProvider } from "./context/DateFormatContext";
import { EventColorProvider } from "./context/EventColorContext";
import { UIPreferencesProvider } from "./context/UIPreferencesContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import { VATProvider } from "./context/VATContext";
import { LayoutProvider } from "./components/layout/LayoutProvider";
import { ToastProvider } from "./components/ui/Toast";
import App from "./App";
import "./index.css";

// Create query client with default options
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 0, // Always stale — ensures mutations invalidate+refetch immediately
			retry: 1,
			refetchOnWindowFocus: false,
		},
	},
});

// Get the root element
const container = document.getElementById("root");

if (!container) {
	throw new Error("Root element not found");
}

// Self-heal open tabs across deploys: a stale bundle that requests a removed
// chunk fires "vite:preloadError". Reload once to pull the new index.html +
// chunks, but guard against a reload loop (assets may 404 while a deploy is
// still propagating) with a 10s floor stored in sessionStorage.
const PRELOAD_RELOAD_KEY = "vite:lastPreloadReload";
window.addEventListener("vite:preloadError", (event) => {
	event.preventDefault();
	const last = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY) ?? 0);
	if (Date.now() - last < 10_000) return;
	sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
	location.reload();
});

// Render the app
createRoot(container).render(
	<StrictMode>
		<BrowserRouter>
			<QueryClientProvider client={queryClient}>
				<ThemeProvider>
					<AuthProvider>
						<LayoutProvider>
							<DateFormatProvider>
								<EventColorProvider>
									<UIPreferencesProvider>
										<OnboardingProvider>
											<VATProvider>
												<AudioPlayerProvider>
													<ToastProvider>
														<App />
													</ToastProvider>
												</AudioPlayerProvider>
											</VATProvider>
										</OnboardingProvider>
									</UIPreferencesProvider>
								</EventColorProvider>
							</DateFormatProvider>
						</LayoutProvider>
					</AuthProvider>
				</ThemeProvider>
			</QueryClientProvider>
		</BrowserRouter>
	</StrictMode>
);